import WorkspacePrimaryButton from "@/components/PrimaryButton";
import { useState, useEffect, useRef } from "react";
import { Classroom, Lesson } from "@/types/tutor";
import api from "@/utils/axios-utils";
import SecondaryButton from "@/components/SecondaryButton";
import { Check } from "lucide-react";
import LessonDrawer from "@/components/workspace/LessonDrawer";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import { useAuthStore } from "@/store/useAuthStore";
import CleverPublishModal from "./CleverPublishModal";
import { lookUpSequencing } from "./sequencingLookup";

interface ClassroomAssignmentProps {
  lesson: Lesson;
  classrooms: Classroom[];
  onClose: () => void;
  onAssignmentSuccess?: () => void;
}

const ClassroomAssignment = ({
  lesson,
  classrooms,
  onClose,
  onAssignmentSuccess,
}: ClassroomAssignmentProps) => {
  const [assignmentStatus, setAssignmentStatus] = useState<{
    [key: number]: boolean;
  }>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const inFlight = useRef(false);
  const { showNotification } = useNotificationStore();
  const [publishClassroomId, setPublishClassroomId] = useState<number | null>(
    null,
  );
  // Auto-sequencing owns assignment for the classrooms it covers. Scoped per
  // classroom, so a teacher may have some sequenced and some not.
  const [sequencedIds, setSequencedIds] = useState<Set<number>>(new Set());
  // A failed sequencing lookup is indistinguishable from "nothing is
  // sequenced" unless it is said out loud: every row would render assignable
  // and the refusal state would never appear. False where the server says the
  // feature is absent, since then there is nothing to be uncertain about.
  const [sequencingUnknown, setSequencingUnknown] = useState(false);
  const cleverEnabledForTeacher = useAuthStore(
    (state) => state.user?.config?.enableCleverLmsIntegration === true,
  );

  // Every group is on auto-sequencing, so there is nothing manual to pick and
  // the spec calls for a plain explanation with a single OK — not a list of
  // dead rows.
  const allSequenced =
    classrooms.length > 0 &&
    classrooms.every((classroom) => sequencedIds.has(classroom.id));

  // Check lesson assignment status for all classrooms
  useEffect(() => {
    let cancelled = false;
    const checkAllAssignments = async () => {
      setLoading(true);
      setLoadError(false);
      setError(null);
      try {
        const [response, sequencing] = await Promise.all([
          api.get(`/classrooms/with-lesson-assignment/${lesson.id}`),
          lookUpSequencing(),
        ]);
        if (cancelled) return;
        setSequencedIds(
          sequencing.kind === "ok" ? sequencing.enabledIds : new Set<number>(),
        );
        setSequencingUnknown(sequencing.kind === "failed");
        const rows: { id: number; has_lesson_assigned: boolean }[] =
          response.data ?? [];
        const status: { [key: number]: boolean } = {};
        rows.forEach((row) => {
          status[row.id] = row.has_lesson_assigned;
        });
        setAssignmentStatus(status);
        setSelected(
          new Set(rows.filter((r) => r.has_lesson_assigned).map((r) => r.id)),
        );
      } catch (error) {
        if (cancelled) return;
        console.error("Error checking lesson assignments:", error);
        setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkAllAssignments();
    return () => {
      cancelled = true;
    };
  }, [lesson.id, retry]);

  const toggleSelected = (classroomId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(classroomId)) {
        next.delete(classroomId);
      } else {
        next.add(classroomId);
      }
      return next;
    });
  };

  const hasChanges = classrooms.some(
    (c) => selected.has(c.id) !== (assignmentStatus[c.id] || false),
  );

  const handleApply = async () => {
    if (inFlight.current || loading || loadError || allSequenced) return;
    const toAssign = classrooms.filter(
      (c) =>
        !sequencedIds.has(c.id) &&
        selected.has(c.id) &&
        !assignmentStatus[c.id],
    );
    const toUnassign = classrooms.filter(
      (c) =>
        !sequencedIds.has(c.id) &&
        !selected.has(c.id) &&
        assignmentStatus[c.id],
    );
    if (toAssign.length === 0 && toUnassign.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    inFlight.current = true;
    setError(null);
    try {
      for (const c of toAssign) {
        await api.post(`/classrooms/${c.id}/lessons`, { lesson_id: lesson.id });
        setAssignmentStatus((prev) => ({ ...prev, [c.id]: true }));
      }
      for (const c of toUnassign) {
        await api.post(`/classrooms/${c.id}/lessons/${lesson.id}/unassign`);
        setAssignmentStatus((prev) => ({ ...prev, [c.id]: false }));
      }
      onAssignmentSuccess?.();
      showNotification(
        "Group assignments updated. <a href='/classrooms' class='font-libre-baskerville italic underline text-black hover:text-black'>View groups</a>",
        NotificationType.SUCCESS,
      );
      onClose();
    } catch (err: any) {
      console.error("Error updating class assignments:", err);
      setError(
        err.response?.data?.detail || "Failed to update group assignments",
      );
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <>
      {publishClassroomId == null && (
        <LessonDrawer
          title="Assign to a group"
          closeLabel="Close assignment"
          eyebrow="Group assignment"
          className="et-compact-dialog et-assignment-dialog"
          onClose={() => {
            if (!inFlight.current) onClose();
          }}
        >
          <div className="flex items-start justify-between mb-1">
            <h2>Assign to a group</h2>
          </div>
          <p className="font-mulish text-sm text-[#687368] mb-5 line-clamp-2">
            {lesson.name}
          </p>

          {loading ? (
            <div
              role="status"
              className="font-mulish text-[#687368] py-6 text-center"
            >
              Loading groups...
            </div>
          ) : loadError ? (
            <div
              role="alert"
              className="font-mulish text-red-700 py-6 text-center"
            >
              <p>Failed to check lesson assignments.</p>
              <SecondaryButton
                type="button"
                className="et-secondary-button"
                onClick={() => setRetry((n) => n + 1)}
              >
                Retry
              </SecondaryButton>
            </div>
          ) : classrooms.length === 0 ? (
            <div className="font-mulish text-[#687368] py-6 text-center">
              No groups available
            </div>
          ) : allSequenced ? (
            <div className="flex flex-col gap-5">
              <p className="font-mulish text-[15px] leading-6 text-[#294B39]">
                Auto-sequencing is on. You can&apos;t manually assign lessons
                while it&apos;s on. Turn off Auto-sequencing from Lesson
                Sequencing to assign manually.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="self-end rounded-[6px] bg-[#28543D] px-6 py-3 font-mulish font-bold text-white"
              >
                OK
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {sequencingUnknown && (
                <p
                  role="status"
                  className="mb-1 rounded-[6px] border border-[#FFD79A] bg-[#FFF4E5] px-3 py-2 font-mulish text-xs font-semibold text-[#9A5B00]"
                >
                  Couldn&apos;t check which groups are on auto-sequencing. Any
                  group that is will refuse the assignment.
                </p>
              )}
              {classrooms.map((classroom) => {
                const isSelected = selected.has(classroom.id);
                // Per-classroom scoping means a teacher can have some sequenced
                // groups and some manual ones; only the sequenced ones close.
                const isSequenced = sequencedIds.has(classroom.id);

                return (
                  <div key={classroom.id} className="flex flex-col gap-2">
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      disabled={saving || isSequenced}
                      title={
                        isSequenced
                          ? "Auto-sequencing is on for this group. Turn it off from Lesson Sequencing to assign manually."
                          : undefined
                      }
                      onClick={() => toggleSelected(classroom.id)}
                      className={`flex items-center justify-between gap-3 px-4 py-3 rounded-[6px] border text-left transition-colors disabled:opacity-60 ${
                        isSelected
                          ? "border-[#28543D] bg-[#FBF8F1]"
                          : "border-[#E5E7EB] hover:border-[#CFE9DF]"
                      }`}
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="font-mulish font-semibold text-[15px] text-[#294B39] truncate">
                          {classroom.name}
                        </span>
                        {isSequenced && (
                          <span className="font-mulish text-[11px] font-bold uppercase tracking-wide text-[#9A5B00]">
                            Auto-sequenced
                          </span>
                        )}
                      </span>
                      <span
                        className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                          isSelected
                            ? "bg-[#28543D] border-[#28543D] text-white"
                            : "border-[#D1D5DB]"
                        }`}
                      >
                        {isSelected && <Check size={14} strokeWidth={3} />}
                      </span>
                    </button>
                    {cleverEnabledForTeacher && (
                      <SecondaryButton
                        disabled={saving}
                        onClick={() => setPublishClassroomId(classroom.id)}
                        size="sm"
                      >
                        Publish to Canvas
                      </SecondaryButton>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {error && (
            <p role="alert" className="mt-4 text-sm text-red-700">
              {error} Your selections are preserved. Retry to apply remaining
              changes.
            </p>
          )}

          {/* The refusal state carries its own single OK; showing the shared
            Cancel/Assign footer under it would offer three actions where the
            spec calls for one acknowledgement. */}
          {!allSequenced && (
            <div className="flex items-center justify-end gap-3 mt-6">
              <SecondaryButton
                type="button"
                onClick={onClose}
                disabled={saving}
                className="et-secondary-button"
              >
                Cancel
              </SecondaryButton>
              <WorkspacePrimaryButton
                type="button"
                onClick={handleApply}
                disabled={saving || loading || loadError || !hasChanges}
                className="et-primary-button"
              >
                {saving && (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white" />
                )}
                Assign
              </WorkspacePrimaryButton>
            </div>
          )}
        </LessonDrawer>
      )}
      {cleverEnabledForTeacher && (
        <CleverPublishModal
          isOpen={publishClassroomId != null}
          onClose={() => setPublishClassroomId(null)}
          lessonId={lesson.id}
          lessonName={lesson.name}
          classrooms={classrooms}
          classroomId={publishClassroomId}
        />
      )}
    </>
  );
};

export default ClassroomAssignment;
